import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/Header';
import withStateDispatch from '../../redux/withStateDispatch';
import Modal from '../../components/Modal';
import { callFBFunction } from '../../utils/firebase';
import { getBrowserHelpLink } from '../../helpers/app';
import { RedeemptionFailIcon } from '../../assets/image/icons';
import SuccessIcon from '../../assets/image/icons/success-icon.svg';
import { getRewardTitle } from '../../helpers/specialGiftConditions';

const ScanQR = ({ setLoader }) => {
  const navigate = useNavigate();

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isScanningRef = useRef(false);

  const helpLink = useMemo(getBrowserHelpLink, []);

  const [apiStatus, setApiStatus] = useState({
    status: false,
    type: '',
    data: null
  });

  const [modal, setModal] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const checkCameraPermission = async () => {
    if (!navigator.permissions) return null; // Not supported in all browsers
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch (e) {
      return null;
    }
  };

  const startScanner = async () => {
    const permission = await checkCameraPermission();
    if (permission === 'denied' || permission === 'prompt') {
      setModal(true);
      return;
    }

    if (!scannerRef.current) return;

    html5QrCodeRef.current = new Html5Qrcode(scannerRef.current.id);

    try {
      // Start with facingMode: 'environment'
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        async (decodedText) => {
          if (decodedText) {
            setLoader(true);

            if (isScanningRef.current) {
              await html5QrCodeRef.current.stop();
              html5QrCodeRef.current.clear();
              isScanningRef.current = false;
            }

            const { data: response } = await callFBFunction('redeemReward', { token: decodedText });

            if (response.status) {
              setApiStatus({
                status: response.data.status === 'redeemed',
                type: response.data.status,
                data: response.data.data || null
              });
            } else {
              // handle system failure if needed
              setApiStatus({
                status: false,
                type: 'error',
                data: null
              });
            }

            setLoader(false);
          }
        },
        (errorMessage) => {
          // This callback is called when a scan error occurs.
          console.warn('QR Code scan error:', errorMessage);
        }
      );

      isScanningRef.current = true;
    } catch (err) {
      console.warn('facingMode:start failed, fallback to cameraId —', err.message);
    }
  };

  const requestCameraAccess = async () => {
    if (!navigator || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissionDenied(true);
      setModal(true);
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setPermissionDenied(false);
      setModal(false);
      startScanner();
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      }
      setModal(true);
    }
  };

  useEffect(() => {
    startScanner();

    // Cleanup on unmount
    return () => {
      if (isScanningRef.current && html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .then(() => {
            html5QrCodeRef.current.clear();
            isScanningRef.current = false;
          })
          .catch((err) => {
            console.warn('Stop failed:', err.message);
          });
      }
    };
  }, []);

  return (
    <div className="flex flex-col max-h-screen h-screen max-w-screen-md mx-auto">
      <Header title="Scan QR" subtitle="Scan QR Code to Redeem" showBack={false} />
      {!apiStatus.status && !apiStatus.type && (
        <div className="relative h-full w-full">
          <div id="qr-reader" ref={scannerRef} />
          <p className="absolute bottom-10 right-5 left-5 text-center font-inter text-primary">
            Point at the customer’s QR code to redeem reward
          </p>
        </div>
      )}

      {apiStatus.status && (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
          {/* Title */}
          <h1 className="text-2xl font-inter font-semibold">Redemption Successful!</h1>

          {/* Success Icon */}
          <img className="w-20 h-20 my-8" src={SuccessIcon} alt="Success" />

          {/* Details Card */}
          <div className="w-full max-w-md bg-white shadow-md border rounded-xl p-5">
            <h2 className="text-xl font-inter font-semibold mb-4">Redemption Details</h2>
            <ul className="space-y-3 text-gray-700">
              {/* Referral Info */}
              {apiStatus?.data?.reward?.type === 'referral' && (
                <>
                  <li>
                    <span className="font-medium">Referral:</span> {apiStatus?.data?.referralName}
                  </li>
                  <li>
                    <span className="font-medium">Referred By:</span> {apiStatus?.data?.ownerName}
                  </li>
                </>
              )}

              {/* Referrer Info */}
              {apiStatus?.data?.reward?.type === 'referrer' && (
                <li>
                  <span className="font-medium">Owned By:</span> {apiStatus?.data?.ownerName}
                </li>
              )}

              {/* Common Details */}
              <li>
                <span className="font-medium">Sales Agent:</span> {apiStatus?.data?.salesAgentName}
              </li>
              {apiStatus?.data && (
                <li>
                  <span className="font-medium">Offer:</span>{' '}
                  {getRewardTitle({
                    type: apiStatus?.data?.reward?.type,
                    refer: apiStatus?.data?.refer || apiStatus?.data,
                    reward: apiStatus?.data?.reward
                  })}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {!apiStatus.status && apiStatus.type && (
        <>
          <div className="flex-1 flex flex-col gap-4 justify-center items-center">
            <h1 className="text-2xl font-inter">
              {apiStatus.type === 'error' && 'System Error'}
              {apiStatus.type === 'invalid_token' && 'Invalid QR'}
              {apiStatus.type === 'expired' && 'Exipred QR'}
              {apiStatus.type === 'already_redeemed' && 'Already Redeemed'}
            </h1>
            <p className="w-[70%] leading-6 text-center text-secondary text-lg font-light font-inter">
              {apiStatus.type === 'error' &&
                'An error occurred while redeeming the reward. Please try again later.'}
              {apiStatus.type === 'invalid_token' && 'Looks like this code is invalid.'}
              {apiStatus.type === 'expired' && 'Looks like this code is exipred.'}
              {apiStatus.type === 'already_redeemed' && 'Looks like this code was already used.'}
            </p>
            <div className="w-[50vw] h-[50vw] mt-10">
              <RedeemptionFailIcon width="100%" height="100%" />
            </div>
          </div>

          <div className="px-5 mb-5">
            <p className="text-[12px] text-tertiary">
              Looking for help? Contact JP Burkemper at{' '}
              <a className="text-[#5046E4]" href="tel:(970) 787-0551">
                (970) 787-0551
              </a>{' '}
              or email{' '}
              <a className="text-[#5046E4]" href="mailto:jp@vinsyt.com">
                jp@vinsyt.com
              </a>
            </p>
            <button
              type="button"
              className="w-full text-lg text-white bg-[#5046E4] rounded-md py-4 mt-2.5"
              onClick={() => {
                setApiStatus({
                  status: false,
                  type: '',
                  data: null
                });
                startScanner();
              }}>
              Retry
            </button>

            <button
              type="button"
              className="w-full text-lg text-quaternary bg-[#E4E7EB] rounded-md py-4 mt-2.5"
              onClick={() => {
                setApiStatus({
                  status: false,
                  type: '',
                  data: null
                });
                startScanner();
              }}>
              Home
            </button>
          </div>
        </>
      )}

      {apiStatus.status && (
        <div className="px-5 mb-5">
          <button
            type="button"
            className="w-full text-lg text-white bg-[#5046E4] rounded-md py-4 mt-2.5"
            onClick={() => {
              setApiStatus({
                status: false,
                type: '',
                data: null
              });
              startScanner();
            }}>
            Done
          </button>
          <button
            type="button"
            className="w-full text-lg text-quaternary bg-[#E4E7EB] rounded-md py-4 mt-2.5"
            onClick={() => {
              navigate('/redemption-history');
            }}>
            View Scan History
          </button>
        </div>
      )}

      <Modal
        modal={modal}
        customPadding
        content={
          permissionDenied ? (
            <>
              Camera access is blocked. Please enable camera access in your browser settings to scan
              QR codes.
              <br />
              <a
                href={helpLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline">
                Learn how to enable camera access
              </a>
            </>
          ) : (
            'Camera access is required to scan QR codes. Please allow camera access.'
          )
        }
        positiveButton={permissionDenied ? undefined : 'Request'}
        onPositiveClick={permissionDenied ? undefined : requestCameraAccess}
      />
    </div>
  );
};

export default withStateDispatch(ScanQR);
